import React, { useState, useRef, useEffect } from 'react';
import { User, Lock, EyeOff, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login } from '../services/api';
import { useAuth } from '../context/AuthContext';

export function Login() {
  const { t } = useTranslation(['pages-misc-1', 'common']);
  const { refresh } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const errorTimerRef = useRef(null);

  // Clear any pending error timer on unmount
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const showError = (message) => {
    // Clear any existing timer first
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError(message);
    // Auto-dismiss error after 5 seconds
    errorTimerRef.current = setTimeout(() => {
      setError('');
    }, 5000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    // Do NOT clear the error here — let showError manage it
    setIsLoading(true);

    try {
      const response = await login({ username, password });
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('isAuthenticated', 'true');

        // Pull the authoritative profile (incl. role) into the auth context so
        // the whole app reflects the correct role immediately.
        try {
          await refresh();
        } catch (profileError) {
          // Non-blocking: context will retry on next mount.
          localStorage.setItem('user', JSON.stringify({ username }));
        }

        navigate('/dashboard');
      }
    } catch (err) {
      // Inputs are NOT cleared — state for username/password is unchanged
      showError(err.response?.data?.detail || t('pages-misc-1:login.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full bg-[#f8f9fb]">
      {/* Left Side - Login Form */}
      <div className="flex w-full flex-col justify-center px-8 lg:w-1/2 lg:px-24 xl:px-32">
        <div className="mx-auto w-full max-w-md">
          {/* Logo */}
          <div className="mb-10 flex items-center justify-center lg:justify-start">
            <div className="flex items-center text-2xl font-bold text-gray-900">
              <svg
                width="32"
                height="32"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M15.5705 3.32576C16.7977 4.99415 17.5371 6.76854 17.5317 8.86647C17.5201 11.1151 16.6176 13.2678 15.0221 14.8534C13.1366 16.6972 10.8897 17.3515 8.31231 17.3069C8.2554 16.5151 8.28647 15.5887 8.2938 14.7841C10.6843 14.3971 12.8278 13.8973 14.0854 11.5566C14.853 10.1284 14.9556 8.42802 14.4502 6.89621C14.3216 6.50626 14.185 5.92757 13.8033 5.72625C9.77919 8.03596 8.89774 9.12255 8.2938 8.92788C7.68986 8.73321 9.02011 6.11928 11.6875 3.74164C10.1075 2.8587 8.59982 2.54476 6.81791 3.04527C5.40705 3.44185 4.21174 4.38319 3.49567 5.66192C2.41043 7.56745 2.69083 9.17627 3.24517 11.1498C2.3654 11.2498 1.39071 11.227 0.498568 11.229C0.408997 10.9706 0.331502 10.7074 0.26618 10.4411C-1.43369 3.72442 5.32133 -1.77285 11.6196 0.535603C12.4761 0.849546 13.059 1.1129 13.8541 1.67933C14.6492 2.24577 15.2649 2.91003 15.5705 3.32576Z"
                  fill="#0076E1"
                />
              </svg>
              {t('pages-misc-1:login.brand')}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200">
                {error}
              </div>
            )}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t('pages-misc-1:login.username')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('pages-misc-1:login.usernamePlaceholder')}
                  className="block w-full rounded-md border border-gray-300 py-2.5 pl-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t('pages-misc-1:login.password')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('pages-misc-1:login.passwordPlaceholder')}
                  className="block w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>



            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md bg-[#0070f3] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center"
            >
              {isLoading ? t('pages-misc-1:login.loggingIn') : t('common:login')}
            </button>
          </form>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2">
        <img
          src="/login-bg.png"
          alt={t('pages-misc-1:login.backgroundAlt')}
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
}
